import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, getUser } from "../api";
import { getCart, addToCart, setQty, removeItem, clearCart, cartCount } from "../lib/cart";
import { toast } from "sonner";
import { ShoppingCart, X, Trash2, Minus, Plus } from "lucide-react";
import i18n from "../i18n";
import { ShareMenu } from "./ShareMenu";

const eur = (c) => new Intl.NumberFormat(i18n.language || "it", { style: "currency", currency: "EUR" }).format((Number.isFinite(c) ? c : 0) / 100);
const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (s) => (s && s.startsWith("/api/") ? IMG_BASE + s : s);
// Varianti reali: il flag has_variants non basta, deve esistere davvero almeno una variante.
const hasRealVariants = (p) => Array.isArray(p?.variants) && p.variants.length > 0;
const finiteVariantPrices = (p) => (p?.variants || []).map((v) => v.eff_price_cents).filter((n) => Number.isFinite(n));
// Prezzo "da" della card / prezzo di riferimento: min varianti valide, altrimenti prezzo base. Mai Infinity/NaN.
const cardPrice = (p) => {
  if (hasRealVariants(p)) { const ps = finiteVariantPrices(p); if (ps.length) return Math.min(...ps); }
  return Number.isFinite(p?.eff_price_cents) ? p.eff_price_cents : 0;
};

export function ShopTab({ orgId, orgName, openCart, openProductId }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [products, setProducts] = useState(null);
  const [cartOpen, setCartOpen] = useState(!!openCart);
  const [count, setCount] = useState(cartCount());
  useEffect(() => {
    api.get(`/organizations/${orgId}/products`).then(({ data }) => setProducts(data)).catch(() => setProducts([]));
    const h = () => setCount(cartCount()); window.addEventListener("cart-changed", h); return () => window.removeEventListener("cart-changed", h);
  }, [orgId]);
  useEffect(() => { if (openCart) setCartOpen(true); }, [openCart]);
  useEffect(() => { if (openProductId) nav(`/products/${openProductId}`); }, [openProductId]); // eslint-disable-line

  return (
    <div data-testid="shop-tab">
      <div className="flex justify-end mb-3">
        <button className="yp-btn yp-btn-outline !py-1.5 !px-4 text-sm relative" onClick={() => setCartOpen(true)} data-testid="shop-open-cart">
          <ShoppingCart className="w-4 h-4 inline mr-1" />{t("shop.cart")}
          {count > 0 && <span className="ml-1 yp-badge yp-badge-paid" data-testid="shop-cart-count">{count}</span>}
        </button>
      </div>
      {products === null ? <div className="text-slate-400">{t("common.loading")}</div>
        : products.length === 0 ? <div className="text-slate-400" data-testid="shop-empty">{t("shop.empty_public")}</div>
          : (<div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="shop-catalog">
            {products.map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} className="yp-card p-3 text-left hover:border-blue-400 transition-colors block" data-testid="shop-product-card">
                {p.images?.[0] && <img src={imgSrc(p.images[0])} alt={p.name} className="w-full h-32 object-cover rounded mb-2" />}
                <div className="font-medium text-sm" data-testid="shop-product-name">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.category}</div>
                <div className="text-sm font-semibold mt-1">{eur(cardPrice(p))}</div>
              </Link>))}
          </div>)}
      {cartOpen && <CartDrawer onClose={() => setCartOpen(false)} />}
    </div>);
}

function ProductModal({ product, orgId, orgName, onClose, onAdded }) {
  const { t } = useTranslation();
  const hasVariants = hasRealVariants(product);
  const attrs = hasVariants ? [...new Set(product.variants.flatMap((v) => Object.keys(v.options || {})))] : [];
  const [opts, setOpts] = useState({});
  const [qty, setQty] = useState(1);
  const matched = hasVariants
    ? product.variants.find((v) => attrs.every((a) => (v.options || {})[a] === opts[a]))
    : null;
  const price = hasVariants ? (matched ? matched.eff_price_cents : cardPrice(product)) : (Number.isFinite(product.eff_price_cents) ? product.eff_price_cents : 0);
  const avail = hasVariants ? (matched ? matched.available : null) : product.available;
  const add = () => {
    if (hasVariants && !matched) { toast.error(t("shop.select_variant")); return; }
    if (avail !== null && qty > avail) { toast.error(t("shop.out_of_stock")); return; }
    const item = { product_id: product.id, variant_id: matched?.id || null, product_name: product.name,
      variant_label: matched?.label || "", unit_price_cents: price, image: matched?.image || product.images?.[0] || null, qty };
    const r = addToCart(orgId, orgName, item);
    if (r.conflict) {
      if (window.confirm(t("shop.vendor_conflict", { org: r.currentOrg }))) { addToCart(orgId, orgName, item, true); toast.success(t("shop.added")); onAdded(); }
      return;
    }
    toast.success(t("shop.added")); onAdded();
  };
  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="shop-product-overlay">
      <div className="yp-card p-5 max-w-lg w-full bg-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="shop-product-modal">
        <div className="flex justify-between items-start"><h3 className="text-lg font-bold" data-testid="shop-modal-name">{product.name}</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        {product.images?.[0] && <img src={imgSrc(product.images[0])} alt={product.name} className="w-full h-48 object-cover rounded my-3" />}
        {product.description && <p className="text-sm text-slate-600">{product.description}</p>}
        <div className="text-lg font-semibold mt-2" data-testid="shop-modal-price">{eur(price)}</div>
        <div className="mt-1"><ShareMenu shareUrl={`${window.location.origin}/p/prodotto-${product.id}`} title={product.name}
          youpam={getUser()?.id === orgId ? { entityType: "product", entityId: product.id, orgId } : undefined} className="!py-1 !px-3 !text-xs" /></div>
        {attrs.map((a) => (
          <div key={a} className="mt-3">
            <label className="text-sm font-medium capitalize">{a}</label>
            <div className="flex gap-2 flex-wrap mt-1">
              {[...new Set(product.variants.map((v) => (v.options || {})[a]).filter(Boolean))].map((val) => (
                <button key={val} onClick={() => setOpts({ ...opts, [a]: val })} data-testid="shop-variant-option"
                  className={`yp-btn !py-1 !px-3 text-xs ${opts[a] === val ? "yp-btn-primary" : "yp-btn-outline"}`}>{val}</button>))}
            </div>
          </div>))}
        {avail !== null && <div className="text-xs text-slate-500 mt-2" data-testid="shop-modal-avail">{avail > 0 ? t("shop.in_stock", { n: avail }) : t("shop.out_of_stock")}</div>}
        <div className="flex items-center gap-2 mt-3">
          <label className="text-sm">{t("shop.qty")}</label>
          <input type="number" min="1" value={qty} onChange={(e) => setQty(Math.max(1, Number(e.target.value)))} className="yp-input !w-20 !py-1" data-testid="shop-qty-input" />
        </div>
        <button className="yp-btn yp-btn-primary w-full mt-4" onClick={add} disabled={avail === 0} data-testid="shop-add-to-cart">{t("shop.add_to_cart")}</button>
      </div>
    </div>);
}

export function CartDrawer({ onClose }) {
  const { t } = useTranslation();
  const u = getUser();
  const [cart, setCart] = useState(getCart());
  const [checkout, setCheckout] = useState(false);
  const [addr, setAddr] = useState({ name: "", line1: "", city: "", zip: "", country: "IT", phone: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [availMap, setAvailMap] = useState({});
  const orgId = cart?.org_id;
  useEffect(() => { const h = () => setCart(getCart()); window.addEventListener("cart-changed", h); return () => window.removeEventListener("cart-changed", h); }, []);
  // Stock disponibile (sola lettura, endpoint esistente) → limite massimo dello stepper. Nessuna modifica alla logica stock.
  useEffect(() => {
    if (!orgId) return;
    api.get(`/organizations/${orgId}/products`).then(({ data }) => {
      const m = {};
      (data || []).forEach((p) => { m[`${p.id}::`] = p.available; (p.variants || []).forEach((v) => { m[`${p.id}::${v.id}`] = v.available; }); });
      setAvailMap(m);
    }).catch(() => {});
  }, [orgId]);
  const maxFor = (i) => { const v = availMap[`${i.product_id}::${i.variant_id || ""}`]; return v === null || v === undefined ? null : v; };
  const dec = (i) => setQty(i.product_id, i.variant_id, Math.max(1, i.qty - 1));
  const inc = (i) => { const mx = maxFor(i); if (mx !== null && i.qty + 1 > mx) { toast.error(t("shop.out_of_stock")); return; } setQty(i.product_id, i.variant_id, i.qty + 1); };
  if (!cart || cart.items.length === 0) return (
    <Drawer title={t("shop.your_cart")} onClose={onClose}>
      <div className="flex flex-col items-center justify-center text-center gap-4 px-6 py-16" data-testid="cart-empty">
        <ShoppingCart className="w-12 h-12 text-slate-300" />
        <div className="text-slate-500">{t("shop.empty_title")}</div>
        <button className="yp-btn yp-btn-primary" onClick={onClose} data-testid="cart-continue-shopping">{t("shop.continue_shopping")}</button>
      </div>
    </Drawer>);
  const subtotal = cart.items.reduce((s, i) => s + i.unit_price_cents * i.qty, 0);
  const startCheckout = () => {
    // Utente non loggato: login richiesto al momento del checkout; il carrello resta preservato in localStorage.
    if (!u) {
      localStorage.setItem("pending_action", JSON.stringify({ type: "shop_checkout", kind: "org", id: orgId }));
      window.location.href = `/login?next=${encodeURIComponent(`/organizations/${orgId}`)}`; return;
    }
    setCheckout(true);
  };
  const pay = async () => {
    if (!u) {
      localStorage.setItem("pending_action", JSON.stringify({ type: "shop_checkout", kind: "org", id: orgId }));
      window.location.href = `/login?next=${encodeURIComponent(`/organizations/${orgId}`)}`; return;
    }
    if (!addr.line1 || !addr.city || !addr.zip) { toast.error(t("shop.address_required")); return; }
    setBusy(true);
    try {
      const items = cart.items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, qty: i.qty }));
      const { data } = await api.post("/shop/create-checkout", { items, shipping_address: addr, customer_note: note.slice(0, 1000), origin_url: window.location.origin });
      clearCart(); window.location.href = data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail || "";
      const key = String(d).startsWith("out_of_stock") ? "shop.out_of_stock"
        : d === "variant_required" ? "shop.select_variant"
        : d === "single_vendor_only" ? "shop.single_vendor"
        : d === "product_unavailable" ? "shop.unavailable"
        : d === "empty_cart" ? "shop.cart_empty"
        : d === "address_required" ? "shop.address_required" : null;
      toast.error(key ? t(key) : t("events.error_generic")); setBusy(false);
    }
  };
  return (
    <Drawer title={t("shop.your_cart")} onClose={onClose}>
      <div className="p-4" data-testid="cart-drawer">
        <div className="text-xs text-slate-500 mb-3">{t("shop.sold_by")}: {cart.org_name}</div>
        <div className="space-y-4">
          {cart.items.map((i) => (
            <div key={`${i.product_id}${i.variant_id}`} className="flex gap-3" data-testid="cart-item">
              {i.image && <img src={imgSrc(i.image)} alt="" className="w-14 h-14 object-cover rounded shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{i.product_name}</div>
                {i.variant_label && <div className="text-xs text-slate-500 truncate">{i.variant_label}</div>}
                <div className="text-xs mt-0.5">{eur(i.unit_price_cents)}</div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <div className="flex items-center rounded-full border border-slate-200" data-testid="cart-item-stepper">
                    <button onClick={() => dec(i)} disabled={i.qty <= 1} aria-label={t("shop.qty")} data-testid="cart-qty-dec"
                      className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-full disabled:opacity-40 hover:bg-slate-100 active:bg-slate-200 transition-colors"><Minus className="w-4 h-4" /></button>
                    <span className="min-w-[2rem] text-center text-sm font-medium tabular-nums" data-testid="cart-item-qty">{i.qty}</span>
                    <button onClick={() => inc(i)} disabled={maxFor(i) !== null && i.qty >= maxFor(i)} aria-label={t("shop.qty")} data-testid="cart-qty-inc"
                      className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-full disabled:opacity-40 hover:bg-slate-100 active:bg-slate-200 transition-colors"><Plus className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => removeItem(i.product_id, i.variant_id)} data-testid="cart-item-remove" aria-label={t("shop.remove")}
                    className="shrink-0 inline-flex items-center justify-center gap-1 h-11 sm:h-9 w-11 sm:w-auto sm:px-3 rounded-full text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors">
                    <Trash2 className="w-4 h-4" /><span className="hidden sm:inline">{t("shop.remove")}</span>
                  </button>
                </div>
              </div>
            </div>))}
        </div>
        <div className="border-t border-slate-100 mt-3 pt-3 text-sm">
          <div className="flex justify-between"><span>{t("shop.subtotal")}</span><span data-testid="cart-subtotal">{eur(subtotal)}</span></div>
        </div>
        {!checkout ? (
          <button className="yp-btn yp-btn-primary w-full mt-4" onClick={startCheckout} data-testid="cart-checkout-btn">{t("shop.checkout")}</button>
        ) : (
          <div className="mt-4 space-y-2" data-testid="checkout-form">
            <h4 className="font-semibold text-sm">{t("shop.shipping_address")}</h4>
            <input className="yp-input" placeholder={t("shop.full_name")} value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="ship-name" />
            <input className="yp-input" placeholder={t("shop.address_line")} value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="ship-line1" />
            <div className="flex gap-2">
              <input className="yp-input" placeholder={t("shop.city")} value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="ship-city" />
              <input className="yp-input !w-28" placeholder={t("shop.zip")} value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} data-testid="ship-zip" />
            </div>
            <div className="flex justify-between text-sm border-t border-slate-100 pt-2"><span className="font-semibold">{t("shop.total_est")}</span><span data-testid="checkout-total">{eur(subtotal)} + {t("shop.shipping_calc")}</span></div>
            <div>
              <label className="text-sm font-semibold">{t("shop.customer_note")}</label>
              <textarea className="yp-input mt-1" rows={3} maxLength={1000} placeholder={t("shop.customer_note_ph")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="checkout-note" />
            </div>
            <button className="yp-btn yp-btn-primary w-full" onClick={pay} disabled={busy} data-testid="checkout-pay-btn">{t("shop.pay")}</button>
          </div>)}
      </div>
    </Drawer>);
}

function Drawer({ title, children, onClose }) {
  const { t } = useTranslation();
  // Blocca lo scroll della pagina sottostante mentre il carrello è aperto e lo ripristina alla chiusura.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" data-testid="cart-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="cart-backdrop" />
      <div className="relative bg-white w-full sm:max-w-md h-full flex flex-col shadow-xl" role="dialog" aria-modal="true">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-white">
          <h3 className="font-bold text-lg truncate" data-testid="cart-title">{title}</h3>
          <button onClick={onClose} aria-label={t("shop.close")} data-testid="cart-close-btn"
            className="shrink-0 -mr-1 w-11 h-11 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 active:bg-slate-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>
      </div>
    </div>);
}
